import React, { createContext, useState, useEffect } from 'react';
import axios from 'axios';

export const AuthContext = createContext();

// No verification needed here — this is only ever used as a UI fallback to
// keep a still-valid session "logged in" client-side; the server re-checks
// the real token on every actual request regardless of what this decodes to.
const decodeJwtPayload = (token) => {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return null;
  }
};

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // A tenant's subscription can lapse mid-session (a card declines, a
  // cancellation goes through) — auth.middleware.js re-checks billing status
  // on every request and returns 402 the instant that happens, same as it
  // already does for user deactivation. This interceptor is on the global
  // axios instance (not a scoped api.js client) because several pages import
  // axios directly rather than through a shared wrapper, so scoping it to
  // just one instance would miss most of the app's requests.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (res) => res,
      (err) => {
        // Only redirect for an already-authenticated session going stale —
        // a 402 on the login request itself (no token yet) means the tenant
        // was already suspended before this browser had a session, and that
        // error is Login.js's to show, not a reason to bounce away from it.
        if (err.response?.status === 402 && localStorage.getItem('token') && window.location.pathname !== '/billing') {
          window.location.href = '/billing';
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  useEffect(() => {
    if (token) {
      axios.get('/api/auth/me', { headers: { 'x-auth-token': token } })
        .then(res => {
          setUser(res.data);
          setLoading(false);
        })
        .catch((err) => {
          if (err.response?.status === 402) {
            // Session itself is fine — only the subscription lapsed. Keep
            // the token and fall back to the JWT's own claims so PrivateRoute
            // treats this as logged-in (the response interceptor above is
            // already sending the browser to /billing; every other route
            // still 402s server-side no matter what the frontend believes).
            const claims = decodeJwtPayload(token);
            if (claims) setUser({ id: claims.id, role: claims.role, outlet: claims.outlet, tenant: claims.tenant });
            setLoading(false);
            return;
          }
          localStorage.removeItem('token');
          setToken(null);
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = async (email, password) => {
    const res = await axios.post('/api/auth/login', { email, password });
    localStorage.setItem('token', res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  // Signup already returns a token + user (immediate access, no separate
  // login round-trip needed) — this just stores that result the same way
  // login() does.
  const setSession = (newToken, newUser) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(newUser);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, setSession, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};
